import { useId } from 'react';
import { format } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';

import { PersonAvatar, initials } from 'components/person-avatar';
import ModalActions from 'components/task-item/modal-actions';
import ModalDeleteTask, { deleteTaskModalId } from 'components/task-item/modal-actions/delete-task';
import { TaskItemProps, useTaskItem } from 'components/task-item/useTaskItem';
import Button from 'components/ui/button';
import { FOCO_VISIVEL } from 'utils/foco';

import { acoesDaMinhaRealizacao } from '../acoes-da-minha-realizacao';
import { FaseDoCiclo } from '../realizacoes-do-semestre';
import { nomeDoParticipante, rostoDoAutor, rostoDoParticipante } from '../identidade-do-participante';
import AgendaDaRealizacao, { etapasDaAgenda, HUMANOGRAMA, PilulaDaEtapa } from './AgendaDaRealizacao';
import AcoesDoParticipante from './AcoesDoParticipante';
import DetalhesDaRealizacao from './DetalhesDaRealizacao';

const MAXIMO_DE_INICIAIS = 5;

interface ParticipanteDoCartao {
  chave: string;
  nome: string;
  rosto: ReturnType<typeof rostoDoParticipante>;
}

/** Participantes do cartão: nome e rosto de `target` quando a leitura os traz, senão as chaves de `team`. */
function participantesDe(data: any): ParticipanteDoCartao[] {
  if (Array.isArray(data.target) && data.target.length) {
    return data.target.map((pessoa: any) => ({
      chave: pessoa._id,
      nome: nomeDoParticipante(pessoa),
      rosto: rostoDoParticipante(pessoa),
    }));
  }
  return Array.isArray(data.team) ? data.team.map((membro: any) => ({ chave: membro._id, nome: membro._id, rosto: {} })) : [];
}

function dataCurta(ms: number): string {
  return format(new Date(ms), 'd MMM. yy', { locale: ptBR });
}

const ACAO_DE_TEXTO = `cursor-pointer border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.04em] hover:underline ${FOCO_VISIVEL}`;

export interface CartaoDaMinhaRealizacaoProps {
  data: any;
  /** Fase mensal em que a realização foi cadastrada (o `cycle` que a página antiga passava ao `TaskItem`). */
  fase: FaseDoCiclo | undefined;
  rotuloDaFase: string;
  playerInfo: any;
  /** `realizacaoEditavel` (regra `taskEditable` de hoje). */
  editavel: boolean;
  /** `podeAtualizarIndicador` (regra `taskCanUpdateIndicator` de hoje). */
  podeAtualizarIndicador: boolean;
  onAtualizarIndicador: () => void;
  onSolicitarAutorizacao: NonNullable<TaskItemProps['onOpenModalSendMessage']>;
  /** Depois de excluir ou quando o "Ver mais" pede a leitura completa: a página relê o ciclo. */
  onRecarregar: () => void;
}

/** Cartão da realização própria (RDN-08…RDN-11) sobre o mesmo modelo do `TaskItem`. */
export default function CartaoDaMinhaRealizacao({
  data,
  fase,
  rotuloDaFase,
  playerInfo,
  editavel,
  podeAtualizarIndicador,
  onAtualizarIndicador,
  onSolicitarAutorizacao,
  onRecarregar,
}: CartaoDaMinhaRealizacaoProps) {
  const modelo = useTaskItem({
    data,
    cycle: fase as TaskItemProps['cycle'],
    playerInfo,
    editable: editavel,
    canUpdateIndicator: podeAtualizarIndicador,
    onUpdateIndicator: onAtualizarIndicador,
    onOpenModalSendMessage: onSolicitarAutorizacao,
    onDelete: onRecarregar,
    onReload: onRecarregar,
  });
  const acoes = acoesDaMinhaRealizacao(modelo);
  const etapas = etapasDaAgenda(modelo);
  const etapaAtual = etapas.find(etapa => etapa.atual);
  const participantes = participantesDe(data);
  const gestorQueAutoriza = data.managers_to_approve?.[0];
  const responsavel = data.original_manager_detail;

  const tituloId = useId();
  const detalhesId = useId();

  const detalhesCarregados = !!data.description && !!data.team && !!data.target;

  function alternarDetalhes() {
    const vaiAbrir = !modelo.oppened;
    if (vaiAbrir && (!data.team || !data.target)) modelo.handleReload();
    modelo.setOppened(vaiAbrir);
  }

  return (
    <article aria-labelledby={tituloId} className="rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
      {acoes.solicitarAutorizacao && (
        <div className="-mx-[26px] -mt-6 mb-5 flex flex-wrap items-center gap-3 rounded-t-jj-lg bg-brand-yellow-light px-[26px] py-3.5">
          <p className="m-0 flex-1 text-xs text-brand-blue-dark">
            {`Número de participantes pendentes de autorização pelo gestor ${gestorQueAutoriza.nome_guerra}`}
          </p>
          <Button
            variant="solido"
            size="sm"
            title={`Solicitar liberação para ${gestorQueAutoriza.nome_guerra} (${gestorQueAutoriza.chave})`}
            onClick={() =>
              onSolicitarAutorizacao({
                managerName: gestorQueAutoriza.nome_guerra,
                participantCount: data?.participants_approval?.total,
                taskTitle: data.title,
                managerId: gestorQueAutoriza.chave,
              })
            }
          >
            Solicitar autorização
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 border-b border-surface-200 pb-4">
        <PersonAvatar
          {...rostoDoAutor(data)}
          initials={initials(data.owner_name_guerra ?? '')}
          size={44}
          initialsClassName="bg-brand-green font-display text-xs font-bold text-brand-navy"
        />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[13px] text-content">
            <a
              href={`${HUMANOGRAMA}${data.owner}`}
              target="_blank"
              rel="noreferrer"
              className={`font-bold text-link hover:text-link-hover ${FOCO_VISIVEL}`}
            >
              {`${data.owner_name_guerra?.split(' ')[0] ?? ''} (${data.owner})`}
            </a>{' '}
            cadastrou · <span className="text-content-soft">{rotuloDaFase}</span>
          </p>
          <p className="m-0 mt-1 text-[11.5px] text-content-soft">
            {`Data do cadastro: ${dataCurta(data.created)}`}
            {!!data.updated && ` · Última atualização: ${dataCurta(data.updated)}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {acoes.atualizarIndicador && (
            <button type="button" onClick={onAtualizarIndicador} className={`${ACAO_DE_TEXTO} text-brand-blue`}>
              Atualizar indicador
            </button>
          )}
          {acoes.excluir && (
            // SPEC_DEVIATION: `text-urgent` no lugar do `brand-pink` do design (2,9:1 no branco).
            <button type="button" onClick={() => modelo.showModal(deleteTaskModalId(data._id))} className={`${ACAO_DE_TEXTO} text-urgent`}>
              Excluir
            </button>
          )}
          {acoes.editar && (
            <button type="button" onClick={modelo.handleEdit} className={`${ACAO_DE_TEXTO} text-brand-blue`}>
              Editar
            </button>
          )}
        </div>
      </div>

      <div className="mb-3 mt-[18px] flex flex-wrap items-center gap-2.5">
        <p className="m-0 font-display text-[13px] font-bold text-brand-blue-dark">Acompanhe o andamento da realização</p>
        <PilulaDaEtapa status={etapaAtual?.status} />
      </div>
      <AgendaDaRealizacao etapas={etapas} />

      <dl className="m-0 mt-5 flex flex-wrap gap-x-[26px] gap-y-4 border-t border-surface-200 pt-4">
        <div className="min-w-0">
          <dt className="mb-1 text-[11px] text-content-soft">Nome da realização</dt>
          <dd className="m-0">
            {/* `leading-[normal]`: o rsuite.min.css global põe `h3 { line-height: 42px }` e o título desceria. */}
            <h3 id={tituloId} className="m-0 leading-[normal] text-[13px] font-bold text-content">
              {data.title || '---'}
            </h3>
          </dd>
        </div>
        {!!responsavel?.nome_guerra && (
          <div>
            <dt className="mb-1 text-[11px] text-content-soft">Responsável pela apreciação</dt>
            <dd className="m-0 text-[13px] font-bold text-content">{modelo.capitalizeWords(responsavel.nome_guerra)}</dd>
          </div>
        )}
        <div>
          <dt className="mb-1 text-[11px] text-content-soft">Pontos recebidos</dt>
          <dd className="m-0 font-display text-base font-bold text-brand-blue">{modelo.points}</dd>
        </div>
        {participantes.length > 0 && (
          <div>
            <dt className="mb-1 text-[11px] text-content-soft">{`Participantes (${participantes.length})`}</dt>
            <dd className="m-0 flex items-center">
              {/* As iniciais são decorativas; o nome de todos vai por extenso ao leitor de tela (WCAG 1.1.1). */}
              <span className="sr-only">{participantes.map(pessoa => pessoa.nome).join(', ')}</span>
              {/* Avatar montado, foto ou iniciais, pela regra do `PersonAvatar` (a mesma do perfil e da Minha Equipe). */}
              {participantes.slice(0, MAXIMO_DE_INICIAIS).map(pessoa => (
                <span
                  key={pessoa.chave}
                  aria-hidden="true"
                  title={pessoa.nome}
                  data-participante-resumo={pessoa.chave}
                  className="-mr-1.5 block rounded-full border-2 border-brand-white"
                >
                  <PersonAvatar
                    {...pessoa.rosto}
                    initials={initials(pessoa.nome)}
                    size={22}
                    initialsClassName="bg-brand-green font-display text-[9px] font-bold text-brand-navy"
                  />
                </span>
              ))}
              {participantes.length > MAXIMO_DE_INICIAIS && (
                <span aria-hidden="true" className="ml-3 text-[11px] font-bold text-content-soft">
                  {`+${participantes.length - MAXIMO_DE_INICIAIS}`}
                </span>
              )}
            </dd>
          </div>
        )}
      </dl>

      <div className="mt-3 text-[11.5px] text-content-soft empty:hidden">{modelo.evaluationData}</div>

      <div id={detalhesId}>
        {modelo.oppened &&
          (detalhesCarregados ? (
            <DetalhesDaRealizacao
              data={data}
              getUserTeam={modelo.getUserTeam}
              getFormattedDate={modelo.getFormattedDate}
              getStepLabel={modelo.getStepLabel}
              ocultarPercentual={modelo.ecoa}
              acoesDoParticipante={pessoa => <AcoesDoParticipante pessoa={pessoa} modelo={modelo} />}
            />
          ) : (
            <p role="status" className="m-0 mt-4 text-center text-xs text-content-soft">
              Carregando informações…
            </p>
          ))}
      </div>

      <button
        type="button"
        aria-expanded={modelo.oppened}
        aria-controls={detalhesId}
        onClick={alternarDetalhes}
        className={`mx-auto mt-4 flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.04em] text-content-soft ${FOCO_VISIVEL}`}
      >
        <span aria-hidden="true" className="text-[13px] leading-none">
          {modelo.oppened ? '−' : '+'}
        </span>
        {`Ver ${modelo.oppened ? 'menos' : 'mais'} informações`}
      </button>

      <ModalActions
        callback={modelo.callBackActions}
        id={data._id}
        status={modelo.status}
        data={data}
        playerInfo={playerInfo}
        validateTeamMember={modelo.validateTeamMember}
        activeEvaluationStep={modelo.activeEvaluationStep as number}
        bonus={modelo.bonus}
        userToChange={modelo.userToChange}
        ecoa={modelo.ecoa}
      />

      <ModalDeleteTask
        taskId={data._id}
        requiresReason={modelo.deleteNeedsReason}
        onDeleted={onRecarregar}
        onRefused={onRecarregar}
      />
    </article>
  );
}
